import { Link, useLinkClickHandler } from 'react-router-dom';
import type { ReactNode } from 'react';
import { Badge, Button, Caption1, Text, makeStyles, mergeClasses } from '@fluentui/react-components';
import type { ChecklistItem, PersonChecklist } from '@vigeo/shared';
import { complianceTokenFor, shellMedia, shellMetrics, tokens } from '@vigeo/ui';
import { StatusChip, formatDue } from './ChecklistView.js';
import { RecordRequirementTrigger, RecordRequirementWhenOpen } from './RecordRequirement.js';
import { RecordingRowProvider } from './RecordingRowProvider.js';
import {
  nextHrAction,
  openBlockers,
  ownerOf,
  ownerPresenters,
  reviewCausePresenters,
  statePresenters,
  summaryOf,
  timingPresenters,
} from './presenters.js';

/**
 * The Person Overview's operational panels. Every number and sentence here is a field of
 * the derived checklist or a presenter in presenters.ts — nothing is computed twice.
 */

const useStyles = makeStyles({
  /** Reference card (Figma 249:6722): flat white, 16px radius, no border, one tinted shadow. */
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadowCard,
  },
  strip: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    [shellMedia.belowTablet]: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
  },
  cell: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderLeft: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    ':first-child': { borderLeft: 'none' },
    [shellMedia.belowTablet]: {
      ':nth-child(3)': { borderLeft: 'none' },
      ':nth-child(n+3)': { borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}` },
    },
  },
  /** Reference figure (Heading/28-Bold on Figma 29:1672). */
  compactFigure: { fontSize: tokens.fontSizeBase500, lineHeight: tokens.lineHeightBase500 },
  compactStrip: { boxShadow: 'none', backgroundColor: tokens.colorNeutralBackground2 },
  figure: { fontFamily: tokens.fontFamilyNumeric, fontSize: tokens.fontSizeHero700, lineHeight: tokens.lineHeightHero700, fontWeight: tokens.fontWeightBold },
  panelHead: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, padding: `${tokens.spacingVerticalMNudge} ${tokens.spacingHorizontalL}`, borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}` },
  /** The one edge that carries meaning: something holds this person up. */
  blockersCard: { border: `${tokens.strokeWidthThin} solid ${tokens.colorComplianceCritical}` },
  row: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    alignItems: 'start',
    ':first-child': { borderTop: 'none' },
    [shellMedia.phone]: { gridTemplateColumns: 'minmax(0, 1fr)' },
  },
  main: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, minWidth: 0 },
  meta: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: tokens.spacingHorizontalS },
  /** On touch widths every control in the row meets the 44px target, whatever its desktop size. */
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    [shellMedia.phone]: { '& > a, & > button, & > div > button': { minHeight: shellMetrics.touchTarget, display: 'inline-flex', alignItems: 'center' } },
  },
  next: {
    [shellMedia.phone]: { '& a, & button': { minHeight: shellMetrics.touchTarget } },
  },
  nextCard: { backgroundColor: tokens.colorBrandBackground2, padding: tokens.spacingHorizontalL, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  reasons: { border: `${tokens.strokeWidthThin} solid ${tokens.colorComplianceAttention}`, padding: tokens.spacingHorizontalL, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  plain: { padding: tokens.spacingHorizontalL, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  h: { fontSize: tokens.fontSizeBase400, lineHeight: tokens.lineHeightBase400, fontWeight: tokens.fontWeightSemibold, margin: 0 },
  empty: { padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}` },
  link: { fontWeight: tokens.fontWeightSemibold },
});

function SectionAction({ to, children }: { readonly to: string; readonly children: ReactNode }) {
  const onClick = useLinkClickHandler<HTMLAnchorElement>(to);
  return <Button as="a" href={to} onClick={onClick}>{children}</Button>;
}

/**
 * One item's Record control with its form beneath it (PG15).
 *
 * Its own provider, unlike the checklist's single one, because these panels show ONE item each —
 * so "only one open at a time" is already true, and a shared coordinator would make the blockers
 * panel and the next-action card close each other's forms for no reason a reader could see.
 */
function RecordInline({ personId, item }: { readonly personId: string; readonly item: ChecklistItem }) {
  return (
    <RecordingRowProvider>
      <RecordRequirementTrigger item={item} />
      <RecordRequirementWhenOpen personId={personId} item={item} />
    </RecordingRowProvider>
  );
}

export function ReadinessSummary({ checklist, compact = false }: { readonly checklist: PersonChecklist; readonly compact?: boolean }) {
  const styles = useStyles();
  const s = summaryOf(checklist);
  const cell = (figure: string, label: string, note: string) => (
    <div className={styles.cell}>
      <span className={mergeClasses(styles.figure, compact && styles.compactFigure)}>{figure}</span>
      <Text weight="semibold">{label}</Text>
      {compact ? null : <Caption1>{note}</Caption1>}
    </div>
  );
  return (
    <section aria-label="Readiness summary" className={mergeClasses(styles.card, styles.strip, compact && styles.compactStrip)}>
      {cell(`${s.met} of ${s.scorable}`, 'Checklist complete', s.unscorable > 0 ? `${s.unscorable} more cannot be scored until HR sets their interval.` : 'Every scorable row counts here.')}
      {cell(String(s.blockers), 'Hold up a first assignment', s.blockers === 0 ? 'Nothing stands in the way.' : 'Known and owed — each names who has it.')}
      {cell(String(s.onHrDesk), 'On HR’s desk', s.onHrDesk === 0 ? 'Nothing waits on a decision.' : 'Chasing the person will not clear these.')}
      {cell(String(s.exceptions), 'Exceptions applied', 'A waiver lifts a gate; it never satisfies a row.')}
    </section>
  );
}

export interface BlockersPanelProps {
  readonly checklist: PersonChecklist;
  readonly canRecord: boolean;
  readonly previewLimit?: number;
  readonly fullListHref?: string;
}

/** "What is holding this person up?" — the engine's blockers, with owner, status and the one action that closes each. */
export function BlockersPanel({ checklist, canRecord, previewLimit, fullListHref }: BlockersPanelProps) {
  const styles = useStyles();
  const blockers = openBlockers(checklist);
  return (
    <section aria-labelledby="blockers-heading" className={mergeClasses(styles.card, blockers.length > 0 && styles.blockersCard)}>
      <div className={styles.panelHead}>
        <h2 id="blockers-heading" className={styles.h}>
          What is holding this person up
        </h2>
        <Caption1>
          {blockers.length === 0
            ? 'Nothing. Every requirement that gates a first assignment is met.'
            : 'Blocking rows that are not yet met. Each names who owes it and what closes it.'}
        </Caption1>
      </div>
      {(previewLimit === undefined ? blockers : blockers.slice(0, previewLimit)).map((item) => {
        const owner = ownerOf(item);
        const timing = timingPresenters[item.timing];
        return (
          <div key={item.requirementKey} className={styles.row}>
            <div className={styles.main}>
              <Text weight="semibold">{item.label}</Text>
              <div className={styles.meta}>
                <Badge appearance="outline" size="small">{ownerPresenters[owner].label}</Badge>
                {timing.canBeOverdue ? null : <Badge appearance="outline" size="small">{timing.label}</Badge>}
                {item.segregated ? <Badge appearance="outline" size="small">Separate file</Badge> : null}
                <Caption1>{item.sources.join(' · ')}</Caption1>
              </div>
              {item.issues.length > 0 ? <Caption1>{reviewCausePresenters[item.issues[0]!].detail}</Caption1> : null}
            </div>
            <div className={styles.actions}>
              {item.dueAt === null ? null : <Caption1>Due {formatDue(item.dueAt)}</Caption1>}
              {owner === 'hr-review' ? (
                <Link className={styles.link} to="/app/review">
                  {canRecord ? 'Review' : 'View review'}
                </Link>
              ) : null}
              {owner === 'hr-decision' ? <Link className={styles.link} to={item.compliance?.reviewQuestion ? canRecord ? '?section=settings' : '?section=documents' : '#trigger-inputs'}>{canRecord ? item.compliance?.reviewQuestion ? 'Review policy decision' : 'Answer' : 'View details'}</Link> : null}
              {owner === 'hr-record' && canRecord ? <RecordInline personId={checklist.personId} item={item} /> : null}
              <StatusChip status={item.status} />
            </div>
          </div>
        );
      })}
      {previewLimit !== undefined && blockers.length > 0 && fullListHref ? <div className={styles.plain}>
        <Caption1>Showing {Math.min(previewLimit, blockers.length)} of {blockers.length} open blockers.</Caption1>
        <SectionAction to={fullListHref}>View full checklist</SectionAction>
      </div> : null}
    </section>
  );
}

export function NextActionCard({ checklist, canRecord }: BlockersPanelProps) {
  const styles = useStyles();
  const action = nextHrAction(checklist);
  return (
    <section aria-labelledby="next-action-heading" className={mergeClasses(styles.card, styles.nextCard, styles.next)}>
      <h2 id="next-action-heading" className={styles.h}>
        Next HR action
      </h2>
      <Text weight="semibold">{action.title}</Text>
      <Caption1>{action.detail}</Caption1>
      {action.kind === 'review' ? (
        // Viewers may look at the queue; the decision controls live there and stay admin-only.
        <Button appearance={canRecord ? 'primary' : 'secondary'} as="a" href="/app/review">
          {canRecord ? 'Open the review queue' : 'View the review queue'}
        </Button>
      ) : null}
      {action.kind === 'answer' ? (
        <SectionAction to={action.item?.compliance?.reviewQuestion ? canRecord ? '?section=settings' : '?section=documents' : '#trigger-inputs'}>{canRecord ? action.item?.compliance?.reviewQuestion ? 'Review policy decision' : 'Answer profile questions' : 'View details'}</SectionAction>
      ) : null}
      {action.kind === 'record' && action.item ? (
        canRecord ? <RecordInline personId={checklist.personId} item={action.item} /> : <Caption1>An admin records this.</Caption1>
      ) : null}
      {action.kind === 'chase' ? <><Caption1>The candidate owns every open blocker.</Caption1><SectionAction to="?section=documents">Open documents and portal access</SectionAction></> : null}
    </section>
  );
}

/** Why this verdict — the engine's review causes, and the verdict's own meaning. */
export function VerdictReasons({ checklist }: { readonly checklist: PersonChecklist }) {
  const styles = useStyles();
  const labels = new Map(checklist.items.map((item) => [item.requirementKey, item.label] as const));
  const state = statePresenters[checklist.state];
  return (
    <section aria-labelledby="verdict-heading" className={mergeClasses(styles.card, styles.reasons)}>
      <h2 id="verdict-heading" className={styles.h}>
        Why “{state.label}”
      </h2>
      <Caption1>{state.meaning}</Caption1>
      {checklist.reviewItems.map((review) => (
        <div key={`${review.requirementKey}-${review.cause}`} className={styles.main}>
          <Text weight="semibold">{reviewCausePresenters[review.cause].label}</Text>
          <Caption1>
            {labels.get(review.requirementKey) ?? 'A requirement not shown on this list'} · {reviewCausePresenters[review.cause].detail}
          </Caption1>
        </div>
      ))}
      {checklist.reviewItems.length === 0 ? <Caption1>Nothing is waiting on an HR decision.</Caption1> : null}
    </section>
  );
}

export function ExceptionsList({ checklist }: { readonly checklist: PersonChecklist }) {
  const styles = useStyles();
  const labels = new Map(checklist.items.map((item) => [item.requirementKey, item.label] as const));
  return (
    <section aria-labelledby="exceptions-heading" className={mergeClasses(styles.card, styles.plain)}>
      <h2 id="exceptions-heading" className={styles.h}>
        Exceptions
      </h2>
      {checklist.appliedExceptions.length === 0 ? (
        <Caption1>None applied. An exception lifts a gate for a period; it never satisfies the requirement, and the gap stays visible.</Caption1>
      ) : (
        checklist.appliedExceptions.map((exception) => (
          <div key={exception.exceptionId} className={styles.main}>
            <Text weight="semibold" style={{ color: complianceTokenFor.warning }}>
              {labels.get(exception.requirementKey) ?? 'Requirement'} — waived
            </Text>
            <Caption1>
              {exception.reason} · granted by {exception.grantedBy}
              {exception.expiresAt === null ? ' · no end date' : ` · until ${formatDue(exception.expiresAt)}`}
            </Caption1>
          </div>
        ))
      )}
    </section>
  );
}
